import { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Building2, User, Loader2 } from "lucide-react";

const SOURCES = [
  { key: "company", label: "Company Directory", endpoint: "/company-directory", people: true },
  { key: "suppliers", label: "Suppliers", endpoint: "/suppliers" },
  { key: "customers", label: "Customers", endpoint: "/customers" },
  { key: "distributors", label: "Distributors", endpoint: "/distributors" },
  { key: "land-freight", label: "Land Freight", endpoint: "/land-freight" },
  { key: "sea-freight", label: "Sea Freight", endpoint: "/sea-freight" },
];

export default function GlobalContactSearch({ onJump }) {
  const [term, setTerm] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const loadedRef = useRef(false);

  const load = async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    const res = await Promise.allSettled(SOURCES.map((s) => api.get(s.endpoint)));
    const byKey = {};
    SOURCES.forEach((s, i) => { byKey[s.key] = res[i].status === "fulfilled" ? res[i].value.data || [] : []; });
    setData(byKey);
    setLoading(false);
  };

  const results = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t || t.length < 2 || !data) return [];
    const out = [];
    for (const s of SOURCES) {
      const hits = [];
      for (const item of data[s.key] || []) {
        if (s.people) {
          if (`${item.name} ${item.role || ""} ${item.email || ""} ${item.phone || ""}`.toLowerCase().includes(t)) {
            hits.push({ id: item.id, title: item.name, sub: [item.role, item.email].filter(Boolean).join(" · "), person: true });
          }
        } else {
          const compHit = (item.company_name || "").toLowerCase().includes(t) || (item.tags || []).some((x) => x.toLowerCase().includes(t));
          const contactHit = (item.contacts || []).find((c) => `${c.name} ${c.title || ""} ${c.email || ""} ${c.phone || ""} ${c.phone_mobile || ""}`.toLowerCase().includes(t));
          if (compHit || contactHit) {
            hits.push({ id: item.id, title: item.company_name, sub: contactHit ? [contactHit.name, contactHit.email || contactHit.phone_mobile || contactHit.phone].filter(Boolean).join(" · ") : "", person: false });
          }
        }
      }
      if (hits.length) out.push({ source: s, hits: hits.slice(0, 5), more: Math.max(0, hits.length - 5) });
    }
    return out;
  }, [term, data]);

  const showPanel = focused && term.trim().length >= 2;
  const jump = (key) => { setFocused(false); onJump(key, term.trim()); };
  return (
    <div className="relative w-full sm:w-80" data-testid="global-contact-search">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        placeholder="Search all contacts…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => { setFocused(true); load(); }}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        className="h-9 rounded-sm pl-8"
        data-testid="global-contact-search-input"
      />
      {showPanel && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-sm border border-slate-200 bg-white shadow-lg" data-testid="global-contact-search-results">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-400" data-testid="global-contact-search-empty">No contacts match “{term.trim()}”.</p>
          ) : (
            results.map((g) => (
              <div key={g.source.key} className="border-b border-slate-100 last:border-0">
                <p className="bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{g.source.label}</p>
                {g.hits.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); jump(g.source.key); }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-[#002FA7]/5"
                    data-testid={`global-search-result-${g.source.key}-${h.id}`}
                  >
                    {h.person ? <User className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> : <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-[#002FA7]" />}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-800">{h.title}</span>
                      {h.sub && <span className="block truncate text-xs text-slate-500">{h.sub}</span>}
                    </span>
                  </button>
                ))}
                {g.more > 0 && (
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); jump(g.source.key); }} className="w-full px-3 pb-2 text-left text-xs font-medium text-[#002FA7] hover:underline">
                    +{g.more} more in {g.source.label}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
